import React from 'react';
import type { BaseInputFieldProps } from '../base-input-field/base-input-field';
import { BaseInputField } from '../base-input-field/base-input-field';
import type { BaseInputWrapperProps } from '../base-input-wrapper/base-input-wrapper';
import { BaseInputWrapper } from '../base-input-wrapper/base-input-wrapper';
import { useControllableState } from '../use-controllable-state';

export type TextInputProps = {
    /**
     * Note that changing the type here to email and tel will also add validation
     * to the input field, meaning that if we want to support both a username and
     * an email, setting type="email" will make the field invalid if it's not an email.
     * If you just want to get a certain keyboard on mobile devices, use the
     * inputMode prop instead. https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/inputmode
     */
    type?: 'text' | 'email' | 'tel';
    noLigatures?: boolean;
} & Omit<BaseInputWrapperProps, 'children'> &
    Omit<BaseInputFieldProps, 'isInvalid' | 'type'>;

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
    function TextInput(
        {
            value,
            defaultValue,
            label,
            hint,
            error,
            help,
            autoComplete = 'on',
            onChange,
            name,
            collapsed,
            className,
            style,
            ...props
        },
        ref,
    ) {
        const [localValue, setLocalValue] = useControllableState<typeof value>({
            prop: value,
            defaultProp: defaultValue || '',
        });

        const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            /**
             * Set local value, needed if the component is not controlled.
             */
            setLocalValue(e.target.value);

            onChange?.(e);
        };

        return (
            <BaseInputWrapper
                label={label}
                hint={hint}
                error={error}
                help={help}
                name={name}
                // so that we can add mt-1 and such to the wrapper
                className={className}
                style={style}
                collapsed={collapsed}>
                <BaseInputField
                    isInvalid={!!error}
                    value={localValue}
                    onChange={handleOnChange}
                    autoComplete={autoComplete}
                    ref={ref}
                    name={name}
                    {...props}
                />
            </BaseInputWrapper>
        );
    },
);
